import React, { useState, useEffect, useCallback } from 'react';
import '../styles/research.css';
import '../styles/navbar.css';
import Papa from 'papaparse';

const ResearchReviewingCommittee = () => {
    const [researchData, setResearchData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('All');
    const [ceoApprovalFilter, setCeoApprovalFilter] = useState('All');
    const [alertMessage, setAlertMessage] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [newResearch, setNewResearch] = useState({
        id: '',
        title: '',
        moderator: '',
        status: 'Pending',
        ceoApproval: 'Pending',
        publishingDate: '',
    });
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    const rowsPerPage = 30;

    // Fetch data from Google Sheets
    const fetchData = useCallback(async () => {
        try {
            const csvURL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vRVkpQeiOG-mhuy5HurM4dU-bYZTcQSHKybqTrYyai0yMbI8dEM60Z1GAMA7q-zJnWRS593cGKUxFjt/pub?gid=0&single=true&output=csv";
            const response = await fetch(csvURL);
            const text = await response.text();

            Papa.parse(text, {
                header: true,
                dynamicTyping: true,
                complete: (result) => {
                    const formattedData = result.data.map((row, index) => ({
                        id: row['Research ID #'] || `REM#${1000 + index}`,
                        title: row['Research Title'] || '',
                        moderator: row['Moderator'] || '',
                        status: row['Status'] || 'Pending',
                        ceoApproval: row['CEO Approval'] || 'Pending',
                        publishingDate: row['Publishing Date'] || '',
                    }));
                    setResearchData(formattedData);
                    setFilteredData(formattedData);
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    showAlertMessage('Failed to parse research data. Please try again.');
                },
            });
        } catch (error) {
            console.error('Error fetching data:', error);
            showAlertMessage('Failed to fetch research data. Please try again.');
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle search and filters
    useEffect(() => {
        const filtered = researchData.filter((row) => {
            const isDate = /^\d{2}\/\d{2}\/\d{4}$/.test(searchQuery);
            const matchesSearch = isDate
                ? row.publishingDate === searchQuery
                : row.title?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStatus = statusFilter === 'All' || row.status === statusFilter;
            const matchesCeoApproval = ceoApprovalFilter === 'All' || row.ceoApproval === ceoApprovalFilter;
            return matchesSearch && matchesStatus && matchesCeoApproval;
        });
        setFilteredData(filtered);
        setCurrentPage(1);
    }, [searchQuery, statusFilter, ceoApprovalFilter, researchData]);

    // Pagination logic
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

    // Handle adding a new research entry
    const handleAddResearch = () => {
        const newResearchWithId = {
            ...newResearch,
            id: `REM#${1000 + researchData.length}`,
        };
        setResearchData([...researchData, newResearchWithId]);
        setFilteredData([...filteredData, newResearchWithId]);
        setNewResearch({
            id: '',
            title: '',
            moderator: '',
            status: 'Pending',
            ceoApproval: 'Pending',
            publishingDate: '',
        });
        setShowAddModal(false);
        showAlertMessage('Research added successfully!');
    };

    // Handle editing a research entry
    const handleEditResearch = (index) => {
        setEditingRow(index);
    };

    // Handle saving edited research entry
    const handleSaveEdit = (index) => {
        const updatedData = [...researchData];
        updatedData[indexOfFirstRow + index] = currentRows[index];
        setResearchData(updatedData);
        setFilteredData(updatedData);
        setEditingRow(null);
        showAlertMessage('Research updated successfully!');
    };

    // Handle deleting a research entry
    const handleDeleteResearch = (id) => {
        const updatedData = researchData.filter((row) => row.id !== id);
        setResearchData(updatedData);
        setFilteredData(updatedData);
        showAlertMessage('Research deleted successfully!');
    };

    // Handle input changes for new research
    const handleNewResearchChange = (e, field) => {
        setNewResearch({ ...newResearch, [field]: e.target.value });
    };

    // Handle input changes for editing research
    const handleEditChange = (e, index, field) => {
        const updatedRows = [...filteredData];
        updatedRows[indexOfFirstRow + index][field] = e.target.value;
        setFilteredData(updatedRows);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const showAlertMessage = (message) => {
        setAlertMessage(message);
        setShowAlert(true);
        setTimeout(() => {
            setShowAlert(false);
        }, 3000);
    };

    // Toggle sidebar state
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Handle logout
    const handleLogout = () => {
        window.location.href = '/';
    };

    return (
        <div className="dashboard-container">
            <div className="particles">
                <div className="particle"></div>
                <div className="particle"></div>
                <div className="particle"></div>
                <div className="particle"></div>
                <div className="particle"></div>
            </div>
            <aside className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
                <div className="sidebar-header">
                    <h1 className="logo" data-text="Innovathrive">Innovathrive</h1>
                    <button onClick={toggleSidebar} className="toggle-button">
                        {isSidebarOpen ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        )}
                    </button>
                </div>
                <nav id="nav-bar">
                    <div className="nav-particles">
                        <div className="nav-particle"></div>
                        <div className="nav-particle"></div>
                        <div className="nav-particle"></div>
                    </div>
                    <div id="nav-header">
                        <a id="nav-title" href="/dashboard" data-text="Research">Research</a>
                        <hr />
                    </div>
                    <div id="nav-content">
                        <div className={`nav-button ${window.location.pathname === '/dashboard' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />
                            </svg>
                            <a href="/dashboard" data-text="Dashboard">Dashboard</a>
                        </div>
                        <div className={`nav-button ${window.location.pathname === '/research' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <a href="/research" data-text="Research Reviewing Committee">Research</a>
                        </div>
                        <div className={`nav-button ${window.location.pathname === '/volunteers' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                            <a href="/volunteers" data-text="Volunteers Board">Volunteers Board</a>
                        </div>
                        <div className={`nav-button ${window.location.pathname === '/futurevolunteers' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="8.5" cy="7" r="4" />
                                <path d="M20 8v6M23 11h-6" />
                            </svg>
                            <a href="/futurevolunteers" data-text="Future Volunteers">Future Volunteers</a>
                        </div>
                        <div className={`nav-button ${window.location.pathname === '/USAID' ? 'active' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                            <a href="/USAID" data-text="USAID">USAID</a>
                        </div>
                        <hr />
                    </div>
                </nav>
            </aside>
            <main className="main-content">
                <header className="dashboard-header">
                    <div className="header-title">
                        <h2 data-text="Research Reviewing Committee">Research Reviewing Committee</h2>
                    </div>
                    <div className="header-actions">
                        <div className="search-bar">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by title or date (DD/MM/YYYY)..."
                            />
                            <span className="search-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                            </span>
                        </div>
                        <button onClick={fetchData} className="refresh-button">
                            <span className="refresh-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00f7ff" strokeWidth="2">
                                    <path d="M23 4v6h-6M1 20v-6h6" />
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                </svg>
                            </span>
                            Refresh
                        </button>
                        <button onClick={handleLogout} className="logout-button">Logout</button>
                    </div>
                </header>
                <div className="filters">
                    <label>Status:</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="All">All</option>
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Completed">Completed</option>
                    </select>
                    <label>CEO Approval:</label>
                    <select value={ceoApprovalFilter} onChange={(e) => setCeoApprovalFilter(e.target.value)}>
                        <option value="All">All</option>
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                    </select>
                </div>
                <section className="dashboard-widgets">
                    <table className="research-table">
                        <thead>
                            <tr>
                                <th>Research ID #</th>
                                <th>Research Title</th>
                                <th>Moderator</th>
                                <th>Status</th>
                                <th>CEO Approval</th>
                                <th>Publishing Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentRows.map((row, index) => (
                                <tr key={row.id}>
                                    <td>{row.id}</td>
                                    <td>
                                        {editingRow === index ? (
                                            <input
                                                type="text"
                                                value={row.title}
                                                onChange={(e) => handleEditChange(e, index, 'title')}
                                            />
                                        ) : (
                                            row.title
                                        )}
                                    </td>
                                    <td>
                                        {editingRow === index ? (
                                            <input
                                                type="text"
                                                value={row.moderator}
                                                onChange={(e) => handleEditChange(e, index, 'moderator')}
                                            />
                                        ) : (
                                            row.moderator
                                        )}
                                    </td>
                                    <td>
                                        {editingRow === index ? (
                                            <select
                                                value={row.status}
                                                onChange={(e) => handleEditChange(e, index, 'status')}
                                            >
                                                <option value="Pending">Pending</option>
                                                <option value="Approved">Approved</option>
                                                <option value="Rejected">Rejected</option>
                                                <option value="Completed">Completed</option>
                                            </select>
                                        ) : (
                                            row.status
                                        )}
                                    </td>
                                    <td>
                                        {editingRow === index ? (
                                            <select
                                                value={row.ceoApproval}
                                                onChange={(e) => handleEditChange(e, index, 'ceoApproval')}
                                            >
                                                <option value="Pending">Pending</option>
                                                <option value="Approved">Approved</option>
                                                <option value="Rejected">Rejected</option>
                                            </select>
                                        ) : (
                                            row.ceoApproval
                                        )}
                                    </td>
                                    <td>
                                        {editingRow === index ? (
                                            <input
                                                type="date"
                                                value={row.publishingDate}
                                                onChange={(e) => handleEditChange(e, index, 'publishingDate')}
                                            />
                                        ) : (
                                            row.publishingDate || 'Not set'
                                        )}
                                    </td>
                                    <td>
                                        {editingRow === index ? (
                                            <button onClick={() => handleSaveEdit(index)}>Save</button>
                                        ) : (
                                            <>
                                                <button onClick={() => handleEditResearch(index)}>Edit</button>
                                                <button onClick={() => handleDeleteResearch(row.id)}>Delete</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
                <div className="pagination">
                    {Array.from({ length: Math.ceil(filteredData.length / rowsPerPage) }, (_, i) => (
                        <button
                            key={i + 1}
                            onClick={() => handlePageChange(i + 1)}
                            className={currentPage === i + 1 ? 'active' : ''}
                        >
                            {i + 1}
                        </button>
                    ))}
                </div>
                <button className="add-button" onClick={() => setShowAddModal(true)}>Add New Research</button>
                {showAddModal && (
                    <div className="modal-overlay">
                        <div className="modal">
                            <h2>Add New Research</h2>
                            <form onSubmit={(e) => { e.preventDefault(); handleAddResearch(); }}>
                                <label>Title:</label>
                                <input
                                    type="text"
                                    value={newResearch.title}
                                    onChange={(e) => handleNewResearchChange(e, 'title')}
                                    required
                                />
                                <label>Moderator:</label>
                                <input
                                    type="text"
                                    value={newResearch.moderator}
                                    onChange={(e) => handleNewResearchChange(e, 'moderator')}
                                    required
                                />
                                <label>Status:</label>
                                <select
                                    value={newResearch.status}
                                    onChange={(e) => handleNewResearchChange(e, 'status')}
                                >
                                    <option value="Pending">Pending</option>
                                    <option value="Approved">Approved</option>
                                    <option value="Rejected">Rejected</option>
                                    <option value="Completed">Completed</option>
                                </select>
                                <label>CEO Approval:</label>
                                <select
                                    value={newResearch.ceoApproval}
                                    onChange={(e) => handleNewResearchChange(e, 'ceoApproval')}
                                >
                                    <option value="Pending">Pending</option>
                                    <option value="Approved">Approved</option>
                                    <option value="Rejected">Rejected</option>
                                </select>
                                <label>Publishing Date:</label>
                                <input
                                    type="date"
                                    value={newResearch.publishingDate}
                                    onChange={(e) => handleNewResearchChange(e, 'publishingDate')}
                                    required
                                />
                                <div className="modal-actions">
                                    <button type="submit">Add Research</button>
                                    <button type="button" onClick={() => setShowAddModal(false)}>Cancel</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
                {showAlert && <div className="alert">{alertMessage}</div>}
            </main>
        </div>
    );
};

export default ResearchReviewingCommittee;